import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

type CleaningRecord = {
    id: number;
    time: string;
    note: string;
    value: number;
    paid: boolean;
};

type MonthGroup = {
    monthKey: string;
    monthLabel: string;
    records: CleaningRecord[];
    total: number;
};

type PaymentConfirmationDialogProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    selectedCleanings: CleaningRecord[];
    currency: string;
    loading?: boolean;
};

const moneyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    maximumFractionDigits: 0,
});

const groupCleaningsByMonth = (cleanings: CleaningRecord[]): MonthGroup[] => {
    const grouped = new Map<string, CleaningRecord[]>();

    for (const cleaning of cleanings) {
        const dt = DateTime.fromSQL(cleaning.time);
        const monthKey = dt.toFormat('yyyy-MM');
        const existing = grouped.get(monthKey) ?? [];
        grouped.set(monthKey, [...existing, cleaning]);
    }

    const monthGroups: MonthGroup[] = [];
    for (const [monthKey, records] of Array.from(grouped.entries())) {
        const sortedRecords = [...records].sort((a, b) => {
            const dateA = DateTime.fromSQL(a.time);
            const dateB = DateTime.fromSQL(b.time);
            return dateA.toMillis() - dateB.toMillis();
        });
        const dt = DateTime.fromFormat(monthKey, 'yyyy-MM');
        monthGroups.push({
            monthKey,
            monthLabel: dt.toFormat('MMMM yyyy'),
            records: sortedRecords,
            total: sortedRecords.reduce((sum, r) => sum + r.value, 0),
        });
    }

    return monthGroups.sort((a, b) => b.monthKey.localeCompare(a.monthKey));
};

const formatDateTime = (timeString: string) => {
    const dt = DateTime.fromSQL(timeString);
    return dt.toLocaleString(DateTime.DATETIME_MED);
};

const PaymentConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    selectedCleanings,
    currency,
    loading = false,
}: PaymentConfirmationDialogProps) => {
    const monthGroups = useMemo(
        () => groupCleaningsByMonth(selectedCleanings),
        [selectedCleanings]
    );

    const overallTotal = useMemo(
        () => selectedCleanings.reduce((sum, r) => sum + r.value, 0),
        [selectedCleanings]
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2.5,
                }}
            >
                Confirm Payment
            </DialogTitle>
            <DialogContent
                sx={{
                    backgroundColor: '#1a1a2e',
                    p: 0,
                }}
            >
                <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                    <Typography variant="body1" sx={{ color: '#9ca3af' }}>
                        You are about to mark the following cleanings as paid:
                    </Typography>

                    {monthGroups.map(group => (
                        <Box key={group.monthKey}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: '2px solid #6366f1',
                                    pb: 1,
                                    mb: 1.5,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: 'primary.main',
                                    }}
                                >
                                    {group.monthLabel}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'primary.main',
                                    }}
                                >
                                    {moneyFormatter.format(group.total)}{' '}
                                    {currency}
                                </Typography>
                            </Box>
                            <Stack spacing={1}>
                                {group.records.map(record => (
                                    <Box
                                        key={record.id}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            pl: 2,
                                            borderLeft: '2px solid #374151',
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {formatDateTime(record.time)}
                                            </Typography>
                                            {record.note && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#9ca3af',
                                                        fontSize: '0.75rem',
                                                    }}
                                                >
                                                    {record.note}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {moneyFormatter.format(
                                                record.value
                                            )}{' '}
                                            {currency}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ))}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderTop: '3px solid #6366f1',
                            pt: 2,
                            mt: 1,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            Total
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 900,
                                color: 'primary.main',
                            }}
                        >
                            {moneyFormatter.format(overallTotal)} {currency}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '3px solid #374151',
                    backgroundColor: '#0f0f23',
                    p: 3,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        borderColor: '#374151',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#374151',
                            backgroundColor: 'rgba(55, 65, 81, 0.1)',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={loading}
                    variant="contained"
                    color="primary"
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                    }}
                >
                    Confirm Payment
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentConfirmationDialog;
